import { apyToAprPercent } from '../../../utils/parsing'
import type { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * 3Jane — `USD3` (senior) and `sUSD3` (junior) supply APR.
 *
 * 3Jane (docs.3jane.xyz) is a credit-based money market on Ethereum:
 * deposited USDC funds unsecured credit — Fintech Credit Conduits and
 * Crypto Credit Lines — with idle cash parked in Aave (waEthUSDC). USD3
 * and sUSD3 are the senior / junior tranche pair over that backing; a
 * fixed `TRANCHE_SHARE_VARIANT` fraction of pool interest goes to the
 * junior, so **both** tranches float with what the backing earns and the
 * junior yields roughly 2x for absorbing first losses.
 *
 * Both tokens are Yearn-v3-strategy ERC-4626s whose `pricePerShare`
 * accrues on-chain, so a rate-delta is *possible* — but it would need
 * archival reads (the exact fragility the Spark spUSDG fetcher was
 * rewritten to shed), and the protocol's own APY page
 * (app.3jane.xyz/info/apy) is a Convex-backed SPA computing 7-day
 * share-price deltas client-side — no stable public JSON surface to pin.
 * DefiLlama indexes both under `project=3jane-lending`, and its TVLs
 * reconcile with on-chain state to the dollar (USD3 pool = supply held
 * outside sUSD3 × pricePerShare; sUSD3 pool = its totalAssets in USDC),
 * so it is tracking exactly the tokens we read. Same DefiLlama-only
 * posture as [reProtocol.ts](./reProtocol.ts): a `0` means "unknown",
 * not "no yield" — `exchangeRate` still shows accrual to date.
 */

/** Registry `yieldKey`s. */
export const USD3_KEY = '3JANE::USD3'
export const SUSD3_KEY = '3JANE::sUSD3'

/**
 * Token-list `assetGroup` keys, emitted alongside the registry keys so
 * the lending join (`intrinsicYields[assetGroup]`) resolves — USD3 is
 * already live Morpho Blue collateral (~$20M market), so the join
 * matters beyond the vault rows.
 *
 * Pinned 2026-08-04 against token-lists: the canonical groups are the
 * GROUP_ALIAS-qualified forms (`USD3::USD3` → `3Jane USD3::USD3`,
 * `sUSD3::sUSD3` → `3Jane Staked USD3::sUSD3`) — the bare on-chain
 * names collide with two unrelated USD3 tickers (Stable.com, Reserve's
 * Web 3 Dollar). The legacy bare keys are emitted too so the join keeps
 * resolving against lists published before the alias landed.
 */
export const USD3_GROUP_KEY = '3Jane USD3::USD3'
export const SUSD3_GROUP_KEY = '3Jane Staked USD3::sUSD3'
const USD3_LEGACY_GROUP_KEY = 'USD3::USD3'
const SUSD3_LEGACY_GROUP_KEY = 'sUSD3::sUSD3'

/** DefiLlama pool UUIDs (`project=3jane-lending`). APYs are the values
 *  at pinning time (2026-08-04) — sanity anchors, not assertions. */
const USD3_POOL = 'f8cd444e-d99f-4132-b234-fd3482bf8806' // 7.01 %
const SUSD3_POOL = 'a99bb965-ebaa-4d98-9ed2-fa18de52c605' // 14.76 %

export const threeJaneFetcher: YieldFetcher = {
  label: 'THREE_JANE',
  fetch: async () => {
    // Independent per tranche — sUSD3 is a tenth of USD3's size and must
    // not be able to take the senior tranche's rate down with it.
    const [usd3, susd3] = await Promise.allSettled([
      fetchDefiLlamaApy(USD3_POOL),
      fetchDefiLlamaApy(SUSD3_POOL),
    ])

    const out: Record<string, number> = {}
    if (usd3.status === 'fulfilled') {
      const apr = apyToAprPercent(usd3.value)
      out[USD3_KEY] = apr
      out[USD3_GROUP_KEY] = apr
      out[USD3_LEGACY_GROUP_KEY] = apr
    }
    if (susd3.status === 'fulfilled') {
      const apr = apyToAprPercent(susd3.value)
      out[SUSD3_KEY] = apr
      out[SUSD3_GROUP_KEY] = apr
      out[SUSD3_LEGACY_GROUP_KEY] = apr
    }
    if (Object.keys(out).length === 0) {
      throw new Error('3Jane: DefiLlama returned no apy for either pool')
    }
    return out
  },
}
